import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from '../../../../../components/forms/Input';
import { Select } from '../../../../../components/forms/Select';
import { RadioGroup } from '../../../../../components/forms/RadioGroup';
import { Button } from '../../../../../components/Button';
import { MaskCPF } from '../../../../../utils/mask';
import { maskMobile } from '../../phone';
import {
  MARITAL_STATUS_OPTIONS,
  MATRIMONIAL_REGIME_OPTIONS,
  YES_NO_OPTIONS,
} from '../../constants';
import {
  maritalRules,
  proponentStepSchema,
  ProponentStepValues,
} from '../../schemas';
import { ICashMeAddress } from '../../types';
import { AddressFields } from '../AddressFields/AddressFields';
import { WizardShell } from '../WizardShell/WizardShell';
import styles from '../WizardShell/WizardShell.module.css';

interface IProponentStep {
  collateralAddress: ICashMeAddress;
  useCollateralAddressDefault: boolean;
  defaultValues: Partial<ProponentStepValues>;
  onBack: () => void;
  onSubmit: (values: ProponentStepValues) => void;
}

export function ProponentStep({
  collateralAddress,
  useCollateralAddressDefault,
  defaultValues,
  onBack,
  onSubmit,
}: IProponentStep) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setFocus,
    formState: { errors },
  } = useForm<ProponentStepValues>({
    resolver: zodResolver(proponentStepSchema),
    mode: 'onBlur',
    defaultValues: {
      useCollateralAddress: useCollateralAddressDefault,
      maritalStatus: undefined,
      matrimonialRegime: '',
      cohabitation: '',
      ...defaultValues,
      address: {
        zipCode: '',
        number: '',
        street: '',
        district: '',
        city: '',
        state: '',
        ...defaultValues.address,
      },
    },
  });

  const maritalStatus = watch('maritalStatus');
  const useCollateralAddress = watch('useCollateralAddress');
  const { asksCohabitation, needsPartner, partnerTitle } = maritalRules(
    maritalStatus,
    watch('cohabitation')
  );

  const submit = handleSubmit((values) =>
    onSubmit(
      values.useCollateralAddress
        ? { ...values, address: collateralAddress as any }
        : values
    )
  );

  return (
    <WizardShell
      step={2}
      totalSteps={4}
      title="Sobre você"
      nextTitle="Renda e finalidade"
      helper="Agora precisamos de alguns dos seus dados pessoais para seguir com a sua análise."
    >
      <form onSubmit={submit} noValidate>
        <h2 className={styles.stepHeading}>Seu endereço</h2>

        <label className={styles.checkbox}>
          <input type="checkbox" {...register('useCollateralAddress')} />
          Moro no endereço do imóvel em garantia.
        </label>

        {!useCollateralAddress && (
          <AddressFields
            prefix="address"
            register={register}
            errors={errors.address}
            setValue={setValue}
            setFocus={setFocus}
          />
        )}

        <h2 className={styles.stepHeading}>Estado civil</h2>

        <div className={styles.field}>
          <Select
            label="Qual o seu estado civil?"
            placeholder="Selecione"
            options={MARITAL_STATUS_OPTIONS}
            errors={errors.maritalStatus?.message}
            {...register('maritalStatus')}
          />
        </div>

        {maritalStatus === 'MARRIED' && (
          <div className={styles.field}>
            <Select
              label="Regime de bens"
              placeholder="Selecione"
              options={MATRIMONIAL_REGIME_OPTIONS}
              errors={errors.matrimonialRegime?.message}
              {...register('matrimonialRegime')}
            />
          </div>
        )}

        {asksCohabitation && (
          <div className={styles.field}>
            <RadioGroup
              name="cohabitation"
              label="Vive em união estável?"
              options={YES_NO_OPTIONS}
              value={watch('cohabitation')}
              onChange={(event) =>
                setValue('cohabitation', event.target.value, {
                  shouldValidate: true,
                })
              }
              errors={errors.cohabitation?.message}
            />
          </div>
        )}

        {needsPartner && (
          <>
            <h2 className={styles.stepHeading}>{partnerTitle}</h2>

            <div className={styles.field}>
              <Input
                type="text"
                label="Nome completo"
                placeholder="Nome completo"
                autoComplete="off"
                errors={errors.spouse?.name?.message}
                {...register('spouse.name')}
              />
            </div>

            <div className={styles.field}>
              <Input
                type="text"
                label="CPF"
                placeholder="000.000.000-00"
                autoComplete="off"
                errors={errors.spouse?.document?.message}
                {...register('spouse.document', {
                  onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                    setValue('spouse.document', MaskCPF(event.target.value)),
                })}
              />
            </div>

            <div className={styles.field}>
              <Input
                type="email"
                label="E-mail"
                placeholder="conjuge@exemplo.com"
                autoComplete="off"
                errors={errors.spouse?.email?.message}
                {...register('spouse.email')}
              />
            </div>

            <div className={styles.field}>
              <Input
                type="text"
                label="Celular"
                placeholder="(00) 90000-0000"
                autoComplete="off"
                errors={errors.spouse?.phone?.message}
                {...register('spouse.phone', {
                  onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                    setValue('spouse.phone', maskMobile(event.target.value)),
                })}
              />
            </div>

            <div className={styles.field}>
              <Input
                type="date"
                label="Data de nascimento"
                errors={errors.spouse?.birthDate?.message}
                {...register('spouse.birthDate')}
              />
            </div>
          </>
        )}

        <div className={styles.footer}>
          <Button type="button" color="flat" text="Voltar" onClick={onBack} />
          <Button type="submit" color="primary" text="Continuar" />
        </div>
      </form>
    </WizardShell>
  );
}
